import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PartnerInfo, StripeConnectStatus } from '../types/partners.ts';
import { toast } from 'sonner';
import { getSession } from './useSession.ts';

export function useCheckStatus(partner: PartnerInfo | null | undefined) {
  return useQuery<StripeConnectStatus>({
    queryKey: ['stripe-connect-status', partner?.id],
    queryFn: async () => {
      const session = await getSession();
      if(!partner) return null;
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-stripe-connect?action=status`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch Stripe Connect status');
      }

      return response.json();
    },
    refetchInterval: 600000, // Refetch every 10 minutes
    refetchIntervalInBackground: false,
    staleTime: 30000, // 30 seconds
  });
}

export function useConnectStripeAccount() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const session = await getSession();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-stripe-connect?action=connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          if (errorData.details) {
            errorMessage += ` - ${errorData.details}`;
          }
        } catch (parseError) {
          try {
            const textContent = await response.text();
            if (textContent) {
              errorMessage = `Response: ${textContent.substring(0, 200)}${textContent.length > 200 ? '...' : ''}`;
            }
          } catch (textError) {
            console.error('Failed to parse error response:', textError);
          }
        }
        throw new Error(errorMessage);
      }

      return response.json();
    },
    onSuccess: (data) => {
      if (data.oauth_url) {
        window.location.href = data.oauth_url;
        toast.success('Redirecting to Stripe...');
      } else {
        toast.success('Stripe Connect account created successfully!');
        queryClient.invalidateQueries({ queryKey: ['stripe-connect-status'] });
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to connect account: ${error.message}`);
    },
  });
}

export function useStripeDashboardLink() {
  return useMutation({
    mutationFn: async () => {
      const session = await getSession();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-stripe-connect?action=dashboard-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create dashboard link');
      }

      return response.json();
    },
    onSuccess: (data) => {
      if (data.dashboard_url) {
        window.open(data.dashboard_url, '_blank');
        toast.success('Stripe dashboard opened in new tab');
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to open dashboard: ${error.message}`);
    },
  });
}
